import {
  getStateFromPath,
  type LinkingOptions,
  type PathConfig,
  type PathConfigMap,
} from '@react-navigation/native';
import { Platform } from 'react-native';
import {
  routes,
  type AuthStackParamList,
  type KioskStackParamList,
  type MainTabParamList,
  type RootStackParamList,
} from '../constants/routes';

type PathParseMap = Record<string, (value: string) => unknown>;

type RoutePathParamSpec = {
  /** Path segments after the route slug, e.g. `:employeeId`. */
  pathSuffix: string;
  parse?: PathParseMap;
};

/**
 * Convert a React Navigation route name (PascalCase) into a URL slug.
 * Keeps path generation dynamic from `routes` instead of hand-maintained strings.
 */
export function routeNameToPathSegment(routeName: string): string {
  return routeName
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1-$2')
    .toLowerCase();
}

const parseNumber = (value: string) => Number(value);

/**
 * Optional path params for screens that expose stable identifiers in the URL.
 * Complex filter objects stay in history state and are not serialized here.
 */
const ROOT_ROUTE_PATH_PARAMS: Partial<
  Record<keyof RootStackParamList, RoutePathParamSpec>
> = {
  EmployeeDetails: {
    pathSuffix: ':employeeId',
    parse: { employeeId: parseNumber },
  },
  EmployeeTimeOffBalance: {
    pathSuffix: ':employeeId',
    parse: { employeeId: parseNumber },
  },
  EmployeeEdit: {
    pathSuffix: ':employeeId',
    parse: { employeeId: parseNumber },
  },
  EmployeeFaceRegistration: {
    pathSuffix: ':employeeId',
    parse: { employeeId: parseNumber },
  },
  EmployeeAssignShift: {
    pathSuffix: ':employeeId',
    parse: { employeeId: parseNumber },
  },
  FaceEnrollmentCamera: {
    pathSuffix: ':employeeId',
    parse: { employeeId: parseNumber },
  },
  AttendanceDetails: {
    pathSuffix: ':attendanceDate',
  },
  LocationTrackingDetails: {
    pathSuffix: ':attendanceId',
    parse: { attendanceId: parseNumber },
  },
  LeaveApproval: {
    pathSuffix: ':workflowInstanceId',
    parse: { workflowInstanceId: parseNumber },
  },
  LeaveBalanceEmployee: {
    pathSuffix: ':employeeId',
    parse: { employeeId: parseNumber },
  },
  LeaveBalanceUpdate: {
    pathSuffix: ':employeeId/:leaveTypeId/:leaveYear',
    parse: {
      employeeId: parseNumber,
      leaveTypeId: parseNumber,
      leaveYear: parseNumber,
    },
  },
  WfhConfigurationCreate: {
    pathSuffix: ':wfhPolicyId?',
    parse: { wfhPolicyId: parseNumber },
  },
  WfhDetails: {
    pathSuffix: ':wfhRequestId',
    parse: { wfhRequestId: parseNumber },
  },
  WfhApproval: {
    pathSuffix: ':workflowInstanceId',
    parse: { workflowInstanceId: parseNumber },
  },
  TimePermissionDetails: {
    pathSuffix: ':permissionRequestId',
    parse: { permissionRequestId: parseNumber },
  },
  TimePermissionApproval: {
    pathSuffix: ':workflowInstanceId',
    parse: { workflowInstanceId: parseNumber },
  },
  AttendanceCorrectionDetails: {
    pathSuffix: ':correctionRequestId',
    parse: { correctionRequestId: parseNumber },
  },
  AttendanceCorrectionApproval: {
    pathSuffix: ':workflowInstanceId',
    parse: { workflowInstanceId: parseNumber },
  },
  WorkflowDetails: {
    pathSuffix: ':workflowInstanceId',
    parse: { workflowInstanceId: parseNumber },
  },
  NotificationDetails: {
    pathSuffix: ':notificationId',
    parse: { notificationId: parseNumber },
  },
  ReportFilter: {
    pathSuffix: ':reportCode',
  },
  ReportPreview: {
    pathSuffix: ':reportCode',
  },
  ReportDownloadResult: {
    pathSuffix: ':downloadId',
  },
  WeeklyAttendanceEmployee: {
    pathSuffix: ':employeeId',
    parse: { employeeId: parseNumber },
  },
  OrganizationCreated: {
    pathSuffix: ':organizationId',
    parse: { organizationId: parseNumber },
  },
  ManagedOrganizationDetails: {
    pathSuffix: ':organizationId',
    parse: { organizationId: parseNumber },
  },
  OrganizationHrAdminCreate: {
    pathSuffix: ':organizationId',
    parse: { organizationId: parseNumber },
  },
  LocationDetails: {
    pathSuffix: ':locationId',
    parse: { locationId: parseNumber },
  },
  LocationEdit: {
    pathSuffix: ':locationId',
    parse: { locationId: parseNumber },
  },
  DepartmentDetails: {
    pathSuffix: ':departmentId',
    parse: { departmentId: parseNumber },
  },
  DepartmentEdit: {
    pathSuffix: ':departmentId',
    parse: { departmentId: parseNumber },
  },
  DesignationDetails: {
    pathSuffix: ':designationId',
    parse: { designationId: parseNumber },
  },
  DesignationEdit: {
    pathSuffix: ':designationId',
    parse: { designationId: parseNumber },
  },
  TeamDetails: {
    pathSuffix: ':teamId',
    parse: { teamId: parseNumber },
  },
  TeamEdit: {
    pathSuffix: ':teamId',
    parse: { teamId: parseNumber },
  },
  ShiftDetails: {
    pathSuffix: ':shiftId',
    parse: { shiftId: parseNumber },
  },
  ShiftEdit: {
    pathSuffix: ':shiftId',
    parse: { shiftId: parseNumber },
  },
  WorkPolicyDetails: {
    pathSuffix: ':workPolicyId',
    parse: { workPolicyId: parseNumber },
  },
  WorkPolicyEdit: {
    pathSuffix: ':workPolicyId',
    parse: { workPolicyId: parseNumber },
  },
  KioskDetails: {
    pathSuffix: ':kioskId',
    parse: { kioskId: parseNumber },
  },
  KioskActivationQr: {
    pathSuffix: ':kioskId',
    parse: { kioskId: parseNumber },
  },
  ProjectEdit: {
    pathSuffix: ':projectId',
    parse: { projectId: parseNumber },
  },
  ProjectTaskBoard: {
    pathSuffix: ':projectId',
    parse: { projectId: parseNumber },
  },
  ProjectTaskDetails: {
    pathSuffix: ':projectId/:taskId',
    parse: { projectId: parseNumber, taskId: parseNumber },
  },
  ProjectReport: {
    pathSuffix: ':projectId',
    parse: { projectId: parseNumber },
  },
  HolidayCalendarEdit: {
    pathSuffix: ':holidayCalendarId',
    parse: { holidayCalendarId: parseNumber },
  },
};

/**
 * Screens owned by nested navigators (or also registered on the root stack
 * for in-app navigate). Linking paths must be unique, so these are only
 * configured under Auth / MainTabs — never duplicated at the root.
 */
const NESTED_ONLY_ROUTE_NAMES = new Set<string>([
  routes.auth,
  routes.login,
  routes.forgotPassword,
  routes.mainTabs,
  routes.dashboard,
  routes.adminDashboard,
  routes.teamDashboard,
  routes.attendance,
  routes.attendanceReport,
  routes.employees,
  routes.leave,
  routes.menu,
  routes.calendarTab,
  routes.kiosk,
  routes.kioskActivation,
  routes.splash,
]);

function buildPathConfigForRoute(
  routeName: string,
): string | PathConfig<Record<string, unknown>> {
  const base = routeNameToPathSegment(routeName);
  const spec = ROOT_ROUTE_PATH_PARAMS[routeName as keyof RootStackParamList];
  if (!spec) {
    return base;
  }
  const path = `${base}/${spec.pathSuffix}`;
  if (!spec.parse) {
    return path;
  }
  return { path, parse: spec.parse };
}

function buildRootStackScreens(): PathConfigMap<RootStackParamList> {
  const screens: Record<string, string | PathConfig<Record<string, unknown>>> =
    {};

  for (const routeName of Object.values(routes)) {
    if (NESTED_ONLY_ROUTE_NAMES.has(routeName)) {
      continue;
    }
    // Tab screens also registered on the root stack keep the same slug.
    screens[routeName] = buildPathConfigForRoute(routeName);
  }

  return screens as PathConfigMap<RootStackParamList>;
}

function buildMainTabScreens(): PathConfigMap<MainTabParamList> {
  const tabRouteNames: Array<keyof MainTabParamList> = [
    routes.dashboard,
    routes.adminDashboard,
    routes.teamDashboard,
    routes.attendance,
    routes.calendarTab,
    routes.attendanceReport,
    routes.employees,
    routes.leave,
    routes.menu,
  ];

  const screens: Record<string, string> = {};
  for (const name of tabRouteNames) {
    screens[name] = routeNameToPathSegment(name);
  }
  return screens as PathConfigMap<MainTabParamList>;
}

function buildAuthScreens(): PathConfigMap<AuthStackParamList> {
  return {
    [routes.login]: routeNameToPathSegment(routes.login),
    [routes.forgotPassword]: routeNameToPathSegment(routes.forgotPassword),
  } as PathConfigMap<AuthStackParamList>;
}

/**
 * Browser / deep-link prefixes. On web, React Navigation syncs History API
 * from the linking config; prefixes are required by the type but unused on web.
 */
export function getLinkingPrefixes(): string[] {
  const browser = globalThis as unknown as {
    location?: { origin?: string };
  };
  const origin = browser.location?.origin;
  if (
    (Platform.OS as string) === 'web' &&
    typeof origin === 'string' &&
    origin.length > 0
  ) {
    return [origin];
  }
  return ['arkhrms://'];
}

export type AppLinkingSession = 'authenticated' | 'unauthenticated';

type AppLinkingConfigOptions = {
  /**
   * Ensures deep links / refresh restore the stack under the focused screen
   * (e.g. MainTabs beneath EmployeeEdit) so Back still works after reload.
   */
  initialRouteName?: typeof routes.mainTabs | typeof routes.auth;
};

/**
 * NavigationContainer must not mount while auth is still initializing.
 * Otherwise linking applies the URL against an empty tree, then the
 * authenticated stack remounts on MainTabs and the refresh looks like a
 * redirect Home.
 */
export function isNavigationBootstrapReady(
  status: 'initializing' | AppLinkingSession,
): boolean {
  return status !== 'initializing';
}

export function buildAppLinkingConfig(
  options: AppLinkingConfigOptions = {},
): NonNullable<LinkingOptions<RootStackParamList>['config']> {
  const initialRouteName =
    options.initialRouteName ?? routes.mainTabs;

  return {
    initialRouteName,
    screens: {
      [routes.auth]: {
        screens: buildAuthScreens(),
      },
      [routes.mainTabs]: {
        path: '',
        screens: buildMainTabScreens(),
      },
      ...buildRootStackScreens(),
    } as PathConfigMap<RootStackParamList>,
  };
}

export function buildKioskLinkingConfig(): NonNullable<
  LinkingOptions<KioskStackParamList>['config']
> {
  return {
    initialRouteName: routes.kiosk,
    screens: {
      [routes.kiosk]: routeNameToPathSegment(routes.kiosk),
      [routes.login]: routeNameToPathSegment(routes.login),
      [routes.forgotPassword]: routeNameToPathSegment(routes.forgotPassword),
    } as PathConfigMap<KioskStackParamList>,
  };
}

/** Build linking options once the auth session is known. */
export function createAppLinking(
  session: AppLinkingSession,
): LinkingOptions<RootStackParamList> {
  return {
    prefixes: getLinkingPrefixes(),
    config: buildAppLinkingConfig({
      initialRouteName:
        session === 'authenticated' ? routes.mainTabs : routes.auth,
    }),
  };
}

export function createKioskLinking(): LinkingOptions<KioskStackParamList> {
  return {
    prefixes: getLinkingPrefixes(),
    config: buildKioskLinkingConfig(),
  };
}

/**
 * Path React Navigation reads on web cold start / refresh.
 * Includes search so query strings survive getStateFromPath when configured.
 */
export function getBrowserLocationPath(
  location: { pathname?: string; search?: string } | null | undefined = (
    globalThis as unknown as { location?: { pathname?: string; search?: string } }
  ).location,
): string {
  if (!location?.pathname) {
    return '/';
  }
  return `${location.pathname}${location.search ?? ''}`;
}

/**
 * Resolve navigation state for a refresh / direct URL open after auth is ready.
 * Returns undefined for paths that do not match any screen (invalid URL).
 */
export function resolveNavigationStateForPath(
  path: string,
  session: AppLinkingSession,
) {
  return getStateFromPath(path, createAppLinking(session).config);
}

/** @deprecated Prefer createAppLinking(session) after auth is ready. */
export const appLinking: LinkingOptions<RootStackParamList> =
  createAppLinking('authenticated');

/** Linking options for the unauthenticated kiosk NavigationContainer. */
export const kioskLinking: LinkingOptions<KioskStackParamList> =
  createKioskLinking();
